import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { decodeHtmlEntities } from "@theme/airbag-decode-html-entities";
import { fetchAirbagProductDetails } from "@theme/airbag-product-details-service";
import {
  findMatchingApplication,
  formatMmyVehicleLabel,
  readMmyVehicleContext,
} from "@theme/mmy-state";

/**
 * @typedef {{ term: string; value: string; html: boolean; preLine?: boolean; listItems?: string[] }} AirbagSpecRow
 */

/**
 * Fetches Airbag Man legacy product details and replaces skeleton UI.
 * Min/max pressure, airbag height (global.weight_rate or global.airbag_height), and warranty
 * come from Liquid (product metafields, global namespace).
 *
 * @typedef {Object} AirbagSpecsRefs
 * @property {HTMLElement} skeleton
 * @property {HTMLElement} panel
 * @property {HTMLDListElement} specList
 * @property {HTMLElement} serverWarrantySource
 * @property {HTMLElement} downloadsWrap
 * @property {HTMLUListElement} downloads
 * @property {HTMLElement} error
 * @property {HTMLElement} empty
 *
 * @extends {Component<AirbagSpecsRefs>}
 */
class AirbagProductSpecs extends Component {
  #requestId = 0;

  /**
   * @param {string} phase
   * @param {unknown} payload
   */
  #logSpecs(phase, payload) {
    if (this.dataset.debugLog !== "true") return;
    if (typeof console === "undefined" || typeof console.log !== "function")
      return;
    console.log("[airbag-product-specs]", phase, payload);
  }

  /**
   * @param {Record<string, unknown>} product
   */
  #fitmentFieldsFromApi(product) {
    return {
      FitmentDetails: product.FitmentDetails ?? null,
      FitmentDescription: product.FitmentDescription ?? null,
      FitmentTime: product.FitmentTime ?? null,
      FittingTime: product.FittingTime ?? null,
      FittingPosition: product.FittingPosition ?? null,
      Fitting: product.Fitting ?? null,
      PARetailsDescription: product.PARetailsDescription ?? null,
      PANote: product.PANote ?? null,
      PAFootNote1: product.PAFootNote1 ?? null,
      PAFootNote2: product.PAFootNote2 ?? null,
      PAFootNote3: product.PAFootNote3 ?? null,
      VehicleName: product.VehicleName ?? null,
      ApplicationCount: Array.isArray(product.Application)
        ? product.Application.length
        : 0,
    };
  }

  /**
   * @param {string} productNr
   * @returns {string}
   */
  #baseProductNr(productNr) {
    const normalized = String(productNr || "")
      .trim()
      .toUpperCase();
    if (normalized.endsWith("HP")) {
      return normalized.slice(0, -2);
    }
    return "";
  }

  connectedCallback() {
    super.connectedCallback();
    const root = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    if (root) {
      root.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    }
    window.addEventListener("storage", this.#onMmyContextChange);
    document.addEventListener("mmy:apply-filter", this.#onMmyContextChange);
    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const root = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    if (root) {
      root.removeEventListener(
        ThemeEvents.variantUpdate,
        this.#onVariantUpdate,
      );
    }
    window.removeEventListener("storage", this.#onMmyContextChange);
    document.removeEventListener("mmy:apply-filter", this.#onMmyContextChange);
    this.#requestId += 1;
  }

  /** @param {Event} event */
  #onMmyContextChange = (event) => {
    if (
      event.type === "storage" &&
      event instanceof StorageEvent &&
      event.key !== "mmy"
    ) {
      return;
    }
    this.#load();
  };

  /**
   * @param {Event & { detail?: { resource?: unknown; data?: { newProduct?: { id?: string }; productId?: string } } }} event
   */
  #onVariantUpdate = (event) => {
    const source = this.dataset.productNrSource;
    if (source !== "sku" && source !== "barcode") return;

    const detail = event.detail;
    if (detail?.data?.newProduct) {
      this.dataset.productId = String(detail.data.newProduct.id);
    }

    if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const resource = detail?.resource;
    if (!resource || typeof resource !== "object") return;

    const variant = /** @type {{ sku?: string; barcode?: string }} */ (
      resource
    );
    this.dataset.initialProductNr =
      source === "sku"
        ? String(variant.sku ?? "")
        : String(variant.barcode ?? "");
    this.#load();
  };

  #currentNr() {
    return (this.dataset.initialProductNr || "").trim();
  }

  /** Product URL includes a collection (e.g. /collections/handle/products/handle). */
  #isCollectionContext() {
    return this.dataset.collectionInUrl === "true";
  }

  /**
   * MMY vehicle context is only used when the product was opened from a collection URL.
   * @returns {import('./mmy-state.js').MmyVehicleContext | null}
   */
  #effectiveMmyContext() {
    if (!this.#isCollectionContext()) return null;
    return readMmyVehicleContext();
  }

  /**
   * Shopify renders min/max pressure and warranty via metafields (see block Liquid).
   */
  #hasServerMetafieldRows() {
    if (
      this.dataset.showMinPressure !== "false" &&
      (this.dataset.serverMinPressure || "").trim()
    ) {
      return true;
    }

    if (
      this.dataset.showMaxPressure !== "false" &&
      (this.dataset.serverMaxPressure || "").trim()
    ) {
      return true;
    }

    if (
      this.dataset.showAirbagHeight !== "false" &&
      (this.dataset.serverAirbagHeight || "").trim()
    ) {
      return true;
    }

    if (this.dataset.showWarranty !== "false") {
      const raw = (this.refs.serverWarrantySource?.innerHTML || "").trim();
      const plain = (this.dataset.serverWarranty || "").trim();
      if (raw || plain) return true;
    }

    return false;
  }

  /**
   * @param {Record<string, unknown>} [product]
   * @returns {{ value: string; html: boolean } | null}
   */
  #resolveWarrantyRow(product) {
    if (this.dataset.showWarranty === "false") return null;

    const fromHtml = (this.refs.serverWarrantySource?.innerHTML || "").trim();
    if (fromHtml) {
      return { value: fromHtml, html: true };
    }

    const fromMetafield = (this.dataset.serverWarranty || "").trim();
    if (fromMetafield) {
      return { value: fromMetafield, html: false };
    }

    if (product) {
      const fromApi = this.#pick(
        product,
        "Warranty",
        "warranty",
        "AirbagWarranty",
        "airbag_warranty",
        "WarrantyTerms",
        "WarrantyDescription",
      );
      if (fromApi) {
        const allowHtml = this.dataset.allowHtml === "true";
        return {
          value: fromApi,
          html: allowHtml && /<[a-z][\s\S]*>/i.test(fromApi),
        };
      }
    }

    return null;
  }

  /**
   * @param {AirbagSpecRow[]} rows
   * @param {string} [productNrForPrefix] - SKU/part number for RR/OA “vehicle payload” row (falls back to data-initial-product-nr).
   * @param {Record<string, unknown>} [product]
   */
  #appendServerMetafieldRows(rows, productNrForPrefix, product) {
    if (this.dataset.showMinPressure !== "false") {
      const v = (this.dataset.serverMinPressure || "").trim();
      if (v) {
        rows.push({
          term: this.dataset.labelMinPressure || "Min. pressure",
          value: v,
          html: false,
        });
      }
    }

    if (this.dataset.showMaxPressure !== "false") {
      const v = (this.dataset.serverMaxPressure || "").trim();
      if (v) {
        rows.push({
          term: this.dataset.labelMaxPressure || "Max. pressure",
          value: v,
          html: false,
        });
      }
    }

    if (this.dataset.showAirbagHeight !== "false") {
      const v = (this.dataset.serverAirbagHeight || "").trim();
      if (v) {
        rows.push({
          term: this.dataset.labelAirbagHeight || "Airbag height",
          value: v,
          html: false,
        });
      }
    }

    this.#maybeAppendVehiclePayloadRow(rows, productNrForPrefix);

    const warranty = this.#resolveWarrantyRow(product);
    if (warranty) {
      rows.push({
        term: this.dataset.labelWarranty || "Warranty",
        value: warranty.value,
        html: warranty.html,
      });
    }
  }

  /**
   * Legacy storefront: extra “Weight → Vehicle payload” line for part numbers starting with RR or OA.
   * @param {AirbagSpecRow[]} rows
   * @param {string} [productNrForPrefix]
   */
  #maybeAppendVehiclePayloadRow(rows, productNrForPrefix) {
    if (this.dataset.showVehiclePayload === "false") return;
    const nr = (productNrForPrefix || this.#currentNr() || "")
      .trim()
      .toUpperCase();
    if (!nr.startsWith("RR") && !nr.startsWith("OA")) return;
    rows.push({
      term: this.dataset.labelWeight || "Weight",
      value: this.dataset.labelVehiclePayload || "Vehicle payload",
      html: false,
    });
  }

  /**
   * @param {AirbagSpecRow[]} rows
   */
  #flushSpecRows(rows) {
    this.#clearEl(this.refs.specList);
    for (const row of rows) {
      const dt = document.createElement("dt");
      dt.className = "airbag-product-specs__term";
      dt.textContent = decodeHtmlEntities(row.term);
      const dd = document.createElement("dd");
      dd.className = "airbag-product-specs__def";
      if (row.listItems && row.listItems.length > 0) {
        const ul = document.createElement("ul");
        ul.className = "airbag-product-specs__def-list";
        for (const item of row.listItems) {
          const li = document.createElement("li");
          if (row.html) {
            li.innerHTML = item;
          } else {
            li.textContent = decodeHtmlEntities(item);
          }
          ul.appendChild(li);
        }
        dd.appendChild(ul);
      } else if (row.html) {
        dd.innerHTML = row.value;
      } else {
        dd.textContent = decodeHtmlEntities(row.value);
        if (row.preLine) {
          dd.classList.add("airbag-product-specs__def--pre-line");
        }
      }
      this.refs.specList.append(dt, dd);
    }
  }

  /**
   * Show only metafield-driven rows plus an optional notice and/or error (no API body).
   * @param {string} notice - Shown below the grid when non-empty (e.g. missing product Nr).
   * @param {string} [errorText] - Shown as role=alert when set (e.g. misconfigured URL or fetch error).
   */
  #presentServerOnly(notice, errorText) {
    /** @type {AirbagSpecRow[]} */
    /** @type {AirbagSpecRow[]} */
    /** @type {AirbagSpecRow[]} */
    const rows = [];
    this.#appendServerMetafieldRows(rows, this.#currentNr());
    this.#flushSpecRows(rows);
    this.#clearEl(this.refs.downloads);
    this.refs.downloadsWrap.hidden = true;
    this.refs.skeleton.hidden = true;

    if (rows.length > 0) {
      this.hidden = false;
      this.refs.panel.hidden = false;
      const showNotice = Boolean((notice || "").trim());
      this.refs.empty.hidden = !showNotice;
      this.refs.empty.textContent = showNotice
        ? decodeHtmlEntities(notice)
        : "";
      if (errorText) {
        this.refs.error.hidden = false;
        this.refs.error.textContent = decodeHtmlEntities(errorText);
      } else {
        this.refs.error.hidden = true;
        this.refs.error.textContent = "";
      }
      this.setAttribute("aria-busy", "false");
      return;
    }

    if (errorText) {
      this.hidden = false;
      this.refs.panel.hidden = true;
      this.refs.empty.hidden = true;
      this.refs.error.hidden = false;
      this.refs.error.textContent = decodeHtmlEntities(errorText);
      this.setAttribute("aria-busy", "false");
      return;
    }

    this.#finishEmpty();
  }

  async #load() {
    const nr = this.#currentNr();
    const base = (this.dataset.apiBase || "").trim();
    const requestId = ++this.#requestId;

    this.hidden = false;
    this.refs.skeleton.hidden = false;
    this.refs.panel.hidden = true;
    this.refs.error.hidden = true;
    this.refs.empty.hidden = true;
    this.setAttribute("aria-busy", "true");

    if (!nr) {
      if (this.#hasServerMetafieldRows()) {
        this.#presentServerOnly(
          this.dataset.emptyNoNr || "Missing product reference.",
        );
      } else {
        this.#finishEmpty();
      }
      return;
    }

    if (!base) {
      if (this.#hasServerMetafieldRows()) {
        this.#presentServerOnly(
          "",
          this.dataset.emptyConfig || "API URL is not configured.",
        );
      } else {
        this.#finishError(
          this.dataset.emptyConfig || "API URL is not configured.",
        );
      }
      return;
    }

    try {
      const mmyContext = this.#effectiveMmyContext();
      this.#logSpecs("load start", {
        productNr: nr,
        apiBase: base,
        mmyContext,
        vehicleParams: mmyContext
          ? {
              make: mmyContext.make,
              model: mmyContext.model,
              cg: mmyContext.chassisGroupId || mmyContext.chassis,
              hg: mmyContext.height,
            }
          : {},
      });

      const vehicleParams = mmyContext
        ? {
            make: mmyContext.make,
            model: mmyContext.model,
            cg: mmyContext.chassisGroupId || mmyContext.chassis,
            hg: mmyContext.height,
          }
        : {};

      const data = await fetchAirbagProductDetails({
        apiBase: base,
        productNr: nr,
        vehicle: vehicleParams,
      });
      if (requestId !== this.#requestId) return;

      const dataRecord = /** @type {Record<string, unknown>} */ (data ?? {});
      const product = /** @type {Record<string, unknown>} */ (
        dataRecord.product ?? dataRecord
      );

      let fitmentTime = this.#pick(
        product,
        "FitmentTime",
        "FittingTime",
        "InstallationTime",
        "fitmentTime",
        "fitment_time",
      );
      const baseProductNr = this.#baseProductNr(nr);
      if (!fitmentTime && baseProductNr) {
        try {
          const baseData = await fetchAirbagProductDetails({
            apiBase: base,
            productNr: baseProductNr,
            vehicle: vehicleParams,
          });
          const baseRecord = /** @type {Record<string, unknown>} */ (
            baseData ?? {}
          );
          const baseProduct = /** @type {Record<string, unknown>} */ (
            baseRecord.product ?? baseRecord
          );
          fitmentTime = this.#pick(
            baseProduct,
            "FitmentTime",
            "FittingTime",
            "InstallationTime",
            "fitmentTime",
            "fitment_time",
          );
          if (fitmentTime) {
            product.FitmentTime = fitmentTime;
            this.#logSpecs("fitment time fallback", {
              productNr: nr,
              baseProductNr,
              fitmentTime,
            });
          }
        } catch (fallbackError) {
          this.#logSpecs("fitment time fallback error", {
            productNr: nr,
            baseProductNr,
            error: fallbackError,
          });
        }
      }

      this.#logSpecs("api response", {
        productNr: nr,
        fitmentFields: this.#fitmentFieldsFromApi(product),
        raw: data,
      });
      this.#renderProduct(product);
    } catch (e) {
      if (requestId !== this.#requestId) return;
      this.#logSpecs("load error", { productNr: nr, error: e });
      const msg =
        `${this.dataset.errorLoad || "Could not load specifications."} ${e?.message ?? ""}`.trim();
      if (this.#hasServerMetafieldRows()) {
        this.#presentServerOnly("", msg);
      } else {
        this.#finishError(msg);
      }
    }
  }

  /**
   * First non-empty string from product for any of the given keys (API naming may vary).
   * @param {Record<string, unknown>} product
   * @param {string[]} keys
   */
  #pick(product, ...keys) {
    for (const key of keys) {
      const v = this.#str(product[key]);
      if (v) return v;
    }
    return "";
  }

  /**
   * @param {string[]} parts
   * @returns {string[]}
   */
  #dedupeFitmentParts(parts) {
    /** @type {string[]} */
    const out = [];
    const seen = new Set();
    for (const part of parts) {
      const trimmed = String(part || "").trim();
      if (!trimmed) continue;
      const key = trimmed.replace(/\s+/g, " ").toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(trimmed);
    }
    return out;
  }

  /**
   * Re-insert line breaks lost when HTML tags are stripped (e.g. Option</p><p>Standard, Kit5"+ lifted).
   * @param {string} text
   * @returns {string}
   */
  #normalizeStrippedHtmlJoins(text) {
    return String(text || "")
      .replace(/([a-z])([A-Z])/g, "$1\n$2")
      .replace(/([A-Za-z])(\d+["'+])/g, "$1\n$2")
      .replace(/\n{2,}/g, "\n")
      .trim();
  }

  /**
   * @param {string} primary
   * @returns {string[]}
   */
  #splitFitmentPrimaryChunks(primary) {
    return String(primary || "")
      .split(/\n+/)
      .map((chunk) => chunk.trim())
      .filter(Boolean);
  }

  /**
   * @param {import('./mmy-state.js').MmyVehicleContext | null} mmyContext
   */
  #resolveFitmentDetails(mmyContext) {
    if (!this.#isCollectionContext()) {
      this.#logSpecs("fitment details skipped", {
        reason: "no collection context",
      });
      return {
        fitmentDetails: "",
        fitmentHtml: false,
        preLine: false,
        parts: [],
      };
    }

    const primary = this.#str(mmyContext?.fitmentDetails);
    if (!primary) {
      this.#logSpecs("fitment details", {
        source: "none",
        fitmentDetails: "",
        mmyContextFitmentDetails: null,
      });
      return {
        fitmentDetails: "",
        fitmentHtml: false,
        preLine: false,
        parts: [],
      };
    }

    const allowHtml = this.dataset.allowHtml === "true";
    let parts = this.#dedupeFitmentParts(
      this.#splitFitmentPrimaryChunks(this.#normalizeStrippedHtmlJoins(primary)),
    );
    const fitmentDetails = parts.join("\n").replace(/\n{2,}/g, "\n");
    const fitmentHtml = allowHtml && /<[a-z][\s\S]*>/i.test(fitmentDetails);
    const preLine =
      parts.length === 1 && !fitmentHtml && fitmentDetails.includes("\n");

    this.#logSpecs("fitment details", {
      source: "mmyContext",
      fitmentDetails,
      fitmentHtml,
      preLine,
      partCount: parts.length,
      parts,
      mmyContextFitmentDetails: primary,
    });

    return { fitmentDetails, fitmentHtml, preLine, parts };
  }

  /**
   * @param {Record<string, unknown>} product
   */
  #hasApiDownloads(product) {
    if (this.dataset.showDownloads === "false") return false;

    const items = product.Downloads;
    if (!Array.isArray(items) || items.length === 0) return false;

    for (const raw of items) {
      if (!raw || typeof raw !== "object") continue;
      const item = /** @type {Record<string, unknown>} */ (raw);
      const href = this.#str(
        item.Url ??
          item.url ??
          item.Link ??
          item.href ??
          item.URL ??
          item.ContentURL ??
          item.contentURL,
      );
      if (href) return true;
    }

    return false;
  }

  /**
   * Whether the API response includes at least one spec field that would be shown,
   * excluding vehicle/application rows and MMY-only sources.
   *
   * @param {Record<string, unknown>} product
   * @param {import('./mmy-state.js').MmyVehicleContext | null} mmyContext
   */
  #hasApiDisplayFields(product, mmyContext) {
    const collectionContext = this.#isCollectionContext();

    if (
      collectionContext &&
      this.dataset.showFitmentDetails !== "false" &&
      this.#str(mmyContext?.fitmentDetails)
    ) {
      return true;
    }

    if (
      collectionContext &&
      this.dataset.showFitting !== "false" &&
      !mmyContext?.fittingPosition
    ) {
      const fitting = this.#pick(
        product,
        "FittingPosition",
        "Fitting",
        "fittingPosition",
        "fitting_position",
      );
      if (fitting) return true;
    }

    if (collectionContext && this.dataset.showFitmentTime !== "false") {
      const fitmentTime = this.#pick(
        product,
        "FitmentTime",
        "FittingTime",
        "InstallationTime",
        "fitmentTime",
        "fitment_time",
      );
      if (fitmentTime) return true;
    }

    if (this.dataset.showWarranty !== "false") {
      const fromHtml = (this.refs.serverWarrantySource?.innerHTML || "").trim();
      const fromMetafield = (this.dataset.serverWarranty || "").trim();
      if (!fromHtml && !fromMetafield) {
        const fromApi = this.#pick(
          product,
          "Warranty",
          "warranty",
          "AirbagWarranty",
          "airbag_warranty",
          "WarrantyTerms",
          "WarrantyDescription",
        );
        if (fromApi) return true;
      }
    }

    if (
      this.dataset.showDescription !== "false" &&
      this.#str(product.Description)
    ) {
      return true;
    }

    if (
      this.dataset.showRetail !== "false" &&
      this.#str(product.PRetailsDescription)
    ) {
      return true;
    }

    if (this.dataset.showNotes !== "false") {
      if (this.#str(product.PANote)) return true;
      for (let i = 1; i <= 3; i++) {
        if (this.#str(product[`PAFootNote${i}`])) return true;
      }
    }

    return this.#hasApiDownloads(product);
  }

  /**
   * @param {Record<string, unknown>} product
   */
  #renderProduct(product) {
    if (!product || typeof product !== "object") {
      product = {};
    }

    const allowHtml = this.dataset.allowHtml === "true";
    const productNrForServer =
      this.#pick(
        product,
        "ProductNr",
        "productNr",
        "ProductNumber",
        "product_number",
      ) || this.#currentNr();

    /** @type {AirbagSpecRow[]} */
    const rows = [];
    const collectionContext = this.#isCollectionContext();
    const mmyContext = collectionContext ? readMmyVehicleContext() : null;
    const matchedApplication =
      mmyContext && product.Application
        ? findMatchingApplication(product.Application, mmyContext)
        : null;

    this.#logSpecs("render start", {
      productNr: productNrForServer,
      mmyContext,
      matchedApplication,
      fitmentFields: this.#fitmentFieldsFromApi(product),
    });

    if (collectionContext) {
      if (mmyContext) {
        const hasApiFields = this.#hasApiDisplayFields(product, mmyContext);
        if (this.dataset.showVehicle !== "false" && hasApiFields) {
          const vehicleLabel =
            formatMmyVehicleLabel(mmyContext) ||
            this.#pick(
              product,
              "VehicleName",
              "vehicleName",
              "Vehicle",
              "vehicle",
            );
          if (vehicleLabel) {
            rows.push({
              term: this.dataset.labelVehicle || "Vehicle",
              value: vehicleLabel,
              html: false,
            });
          }
        } else if (this.dataset.showVehicle !== "false" && !hasApiFields) {
          this.#logSpecs("vehicle row omitted", {
            reason: "no api fields",
          });
        }
      } else {
        this.#appendApplicationRows(rows, product);

        if (this.dataset.showVehicle !== "false") {
          const v = this.#pick(
            product,
            "VehicleName",
            "vehicleName",
            "Vehicle",
            "vehicle",
          );
          if (v)
            rows.push({
              term: this.dataset.labelVehicle || "Vehicle",
              value: v,
              html: false,
            });
        }
      }
    }

    if (this.dataset.showFitmentDetails !== "false" && collectionContext) {
      const { fitmentDetails, fitmentHtml, preLine, parts } =
        this.#resolveFitmentDetails(mmyContext);
      if (fitmentDetails) {
        /** @type {AirbagSpecRow} */
        const fitmentRow = {
          term: this.dataset.labelFitmentDetails || "Fitment details",
          value: fitmentDetails,
          html: fitmentHtml,
        };
        if (parts.length > 1) {
          fitmentRow.listItems = parts;
        } else if (preLine) {
          fitmentRow.preLine = true;
        }
        rows.push(fitmentRow);
      } else {
        this.#logSpecs("fitment details row omitted", {
          reason: "empty value after resolve",
        });
      }
    } else if (this.dataset.showFitmentDetails === "false") {
      this.#logSpecs("fitment details skipped", {
        reason: "show_fitment_details disabled",
      });
    }

    if (this.dataset.showFitting !== "false" && collectionContext) {
      let fitting = "";
      if (mmyContext?.fittingPosition) {
        fitting = mmyContext.fittingPosition;
      } else {
        fitting = this.#pick(
          product,
          "FittingPosition",
          "Fitting",
          "fittingPosition",
          "fitting_position",
        );
      }
      if (fitting) {
        rows.push({
          term: this.dataset.labelFitting || "Fitting position",
          value: fitting,
          html: false,
        });
      }
    }

    if (this.dataset.showFitmentTime !== "false" && collectionContext) {
      const v = this.#pick(
        product,
        "FitmentTime",
        "FittingTime",
        "InstallationTime",
        "fitmentTime",
        "fitment_time",
      );
      if (v) {
        rows.push({
          term: this.dataset.labelFitmentTime || "Fitment time",
          value: v,
          html: false,
        });
      }
    }

    this.#appendServerMetafieldRows(rows, productNrForServer, product);

    if (this.dataset.showDescription !== "false") {
      const v = this.#str(product.Description);
      if (v)
        rows.push({
          term: this.dataset.labelDescription || "Description",
          value: v,
          html: allowHtml,
        });
    }

    if (this.dataset.showRetail !== "false") {
      const a = this.#str(product.PRetailsDescription);
      if (a)
        rows.push({
          term: this.dataset.labelRetail || "Retail",
          value: a,
          html: allowHtml,
        });
    }

    if (this.dataset.showNotes !== "false") {
      const noteLabel = this.dataset.labelNote || "Note";
      const footLabel = this.dataset.labelFootnote || "Footnote";

      const panote = this.#str(product.PANote);
      if (panote) rows.push({ term: noteLabel, value: panote, html: false });

      for (let i = 1; i <= 3; i++) {
        const key = `PAFootNote${i}`;
        const v = this.#str(product[key]);
        if (v) rows.push({ term: `${footLabel} ${i}`, value: v, html: false });
      }
    }

    this.#flushSpecRows(rows);

    if (this.dataset.showDownloads !== "false") {
      this.#renderDownloads(product.Downloads);
    } else {
      this.#clearEl(this.refs.downloads);
      this.refs.downloadsWrap.hidden = true;
    }

    const hasBody = rows.length > 0 || !this.refs.downloadsWrap.hidden;

    this.#logSpecs("render complete", {
      productNr: productNrForServer,
      rowCount: rows.length,
      rows: rows.map((row) => ({
        term: row.term,
        value: row.html ? "(html)" : row.value,
      })),
      hasDownloads: !this.refs.downloadsWrap.hidden,
      hasBody,
    });

    if (!hasBody) {
      this.#finishEmpty();
      return;
    }

    this.hidden = false;
    this.refs.skeleton.hidden = true;
    this.refs.panel.hidden = false;
    this.refs.empty.hidden = true;
    this.refs.error.hidden = true;
    this.setAttribute("aria-busy", "false");
  }

  /**
   * @param {unknown} value
   */
  #str(value) {
    if (value == null) return "";
    const s = String(value).trim();
    return s;
  }

  /**
   * @param {string} value
   */
  #escapeHtml(value) {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  /** @param {HTMLElement | HTMLUListElement} el */
  #clearEl(el) {
    while (el.firstChild) el.removeChild(el.firstChild);
  }

  /**
   * @param {unknown} raw
   * @returns {unknown[]}
   */
  #normalizeArray(raw) {
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") return [raw];
    return [];
  }

  /**
   * @param {AirbagSpecRow[]} rows
   * @param {Record<string, unknown>} product
   */
  #appendApplicationRows(rows, product) {
    if (this.dataset.showApplication === "false") return;

    const items = this.#normalizeArray(product.Application);
    if (items.length === 0) return;
    const lines = [];

    for (const rawRow of items) {
      if (!rawRow || typeof rawRow !== "object") continue;
      const row = /** @type {Record<string, unknown>} */ (rawRow);
      const make = this.#pick(row, "Make", "make");
      const model = this.#pick(
        row,
        "Model",
        "model",
        "ModelChassis",
        "Chassis",
        "ModelAndChassis",
        "modelChassis",
      );
      if (!make && !model) continue;
      lines.push([make, model].filter(Boolean).join(" "));
    }

    if (lines.length === 0) return;
    rows.push({
      term: this.dataset.labelVehicle || "Vehicle",
      value: lines
        .map((line) => this.#escapeHtml(decodeHtmlEntities(line)))
        .join("<br>"),
      html: true,
    });
  }

  /**
   * @param {unknown} items
   */
  #renderDownloads(items) {
    this.#clearEl(this.refs.downloads);
    this.refs.downloadsWrap.hidden = true;

    if (!Array.isArray(items) || items.length === 0) return;

    for (const raw of items) {
      if (!raw || typeof raw !== "object") continue;
      const item = /** @type {Record<string, unknown>} */ (raw);
      const href = this.#str(
        item.Url ??
          item.url ??
          item.Link ??
          item.href ??
          item.URL ??
          item.ContentURL ??
          item.contentURL,
      );
      const label =
        this.#str(
          item.ContentDescription ??
            item.contentDescription ??
            item.Title ??
            item.Name ??
            item.Label ??
            item.Description ??
            item.filename ??
            item.FileName ??
            item.fileName,
        ) || href;

      if (!href) continue;

      const li = document.createElement("li");
      const a = document.createElement("a");
      a.href = href;
      a.textContent = decodeHtmlEntities(label);
      a.rel = "noopener noreferrer";
      if (this.dataset.downloadsNewTab === "true") {
        a.target = "_blank";
      }
      li.appendChild(a);
      this.refs.downloads.appendChild(li);
    }

    this.refs.downloadsWrap.hidden =
      this.refs.downloads.childElementCount === 0;
  }

  /**
   * @param {string} message
   */
  #finishError(message) {
    this.hidden = false;
    this.refs.skeleton.hidden = true;
    this.refs.panel.hidden = true;
    this.refs.empty.hidden = true;
    this.refs.error.hidden = false;
    this.refs.error.textContent = decodeHtmlEntities(message);
    this.setAttribute("aria-busy", "false");
  }

  /** No spec content: hide the block entirely (no placeholder copy). */
  #finishEmpty() {
    this.refs.skeleton.hidden = true;
    this.refs.panel.hidden = true;
    this.refs.error.hidden = true;
    this.refs.empty.hidden = true;
    this.refs.empty.textContent = "";
    this.setAttribute("aria-busy", "false");
    this.hidden = true;
  }
}

if (!customElements.get("airbag-product-specs")) {
  customElements.define("airbag-product-specs", AirbagProductSpecs);
}
